import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { IntlProvider } from "react-intl";
import { useToast } from "@chakra-ui/react";

import { fetchMessages, defaultLanguage } from "./utils";

const LanguageContext = createContext({
  setLanguage: () => {},
});
export const useLanguageContext = () => useContext(LanguageContext);

export default function LanguageProvider({ children }) {
  const toast = useToast();
  const [isInitCompleted, setInitCompleted] = useState(false);
  const [selectedLocale, setSelectedLocale] = useState(defaultLanguage);
  const [error, setError] = useState(null);
  const [{ messages, locale }, setMessages] = useState({
    messages: null,
    locale: defaultLanguage,
  });

  useEffect(() => {
    toast.closeAll(); // clear any leftover toasts as they will pop up again after changing language
    setError(null);
    // Cancel fetch if current locale/messages is already up to date with selectedLocale
    if (locale === selectedLocale && messages) {
      return;
    }
    if (selectedLocale === defaultLanguage) {
      setMessages({
        messages: null,
        locale: selectedLocale,
      });
    } else {
      let shouldCancel = false;
      fetchMessages(selectedLocale)
        .then((messages) => {
          if (!shouldCancel) {
            setMessages({ messages, locale: selectedLocale });
          }
        })
        .catch((e) => {
          if (!shouldCancel) {
            // we don't want setError and setSelectedLocale to be batched together for an update
            // TODO: re-write parts of the logic in this file so that we're allowed to do batch updates
            setError(e);
            setTimeout(() => setSelectedLocale(locale), 0);
          }
        });
      return () => {
        shouldCancel = true;
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale, messages, selectedLocale]); // specifically not including 'toast' as a dependency as it gets updated a lot and triggers a loop on the useEffect

  const onInit = useCallback(() => {
    setInitCompleted(true);
  }, []);

  const value = useMemo(
    () => ({
      setLanguage: setSelectedLocale,
      locale,
      error,
      selectedLocale,
      onInit,
      isInitCompleted,
    }),
    [locale, error, selectedLocale, onInit, isInitCompleted]
  );

  return (
    <LanguageContext.Provider value={value}>
      <IntlProvider messages={messages} locale={locale} defaultLocale={defaultLanguage}>
        {children}
      </IntlProvider>
    </LanguageContext.Provider>
  );
}
